/* eslint-disable react-hooks/exhaustive-deps */
/**
 * FleetAnalyticsFuelIntelligence.js — CORRECT running balance model
 *
 * KEY PRINCIPLE:
 *   Fuel added ≠ fuel consumed. We NEVER divide litres-added by km.
 *   Instead we track a running estimated tank level per vehicle:
 *     tank_after = tank_before - (km × baseline_rate / 100) + litres_added
 *   Anomaly = when tank goes NEGATIVE (physically impossible).
 *   "Theft risk" = negative balance = more fuel consumed than the tank had.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Spin, message, Select, Button, DatePicker, Tag, Tooltip } from 'antd';
import { DownloadOutlined, ReloadOutlined, InfoCircleOutlined,
         WarningOutlined, CheckCircleOutlined } from '@ant-design/icons';
import {
    LineChart, Line, BarChart, Bar,
    XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
    Legend, ResponsiveContainer, Cell, ReferenceLine, LabelList,
} from 'recharts';
import FleetService from '../../../services/FleetService';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import moment from 'moment';

const NAVY = '#1a3a5c';
const toN  = v => Number(v || 0);
const fmtL = v => `${toN(v).toFixed(1)}L`;

// ── Baseline L/100km by fuel_type + engine_cc ─────────────────────────────
// Manufacturer averages for sub-Saharan Africa (mixed tar/gravel, loaded, older roads)
function getBaselineRate(fuelType, cc) {
    const ft = (fuelType || '').toLowerCase();
    const n  = toN(cc) || 2000;
    if (ft.includes('diesel')) {
        if (n < 1600) return { rate: 7.0,  label: 'Diesel <1.6L' };
        if (n < 2000) return { rate: 8.0,  label: 'Diesel 1.6-2.0L' };
        if (n < 2500) return { rate: 9.5,  label: 'Diesel 2.0-2.5L' };
        if (n < 3000) return { rate: 11.0, label: 'Diesel 2.5-3.0L' };
        return             { rate: 13.0, label: 'Diesel >3.0L' };
    }
    if (ft.includes('hybrid'))   return { rate: 6.0,  label: 'Hybrid' };
    if (ft.includes('electric')) return { rate: 0,    label: 'Electric' };
    // Petrol (default)
    if (n < 1000) return { rate: 7.0,  label: 'Petrol <1.0L' };
    if (n < 1300) return { rate: 7.5,  label: 'Petrol 1.0-1.3L' };
    if (n < 1600) return { rate: 8.0,  label: 'Petrol 1.3-1.6L' };
    if (n < 2000) return { rate: 9.0,  label: 'Petrol 1.6-2.0L' };
    if (n < 2500) return { rate: 11.0, label: 'Petrol 2.0-2.5L' };
    if (n < 3000) return { rate: 13.0, label: 'Petrol 2.5-3.0L' };
    return             { rate: 15.0, label: 'Petrol >3.0L' };
}

// ── KPI Card ──────────────────────────────────────────────────────────────
function KPI({ label, value, color = NAVY, sub, warn }) {
    return (
        <div style={{ background:'#fff', border:`1px solid ${warn ? '#ffa39e' : '#e8e8e8'}`,
            borderRadius:8, padding:'12px 16px', borderTop:`3px solid ${color}`, flex:1, minWidth:120 }}>
            <div style={{ fontSize:11, color:'#888', marginBottom:3 }}>{label}</div>
            <div style={{ fontSize:20, fontWeight:700, color }}>{value}</div>
            {sub && <div style={{ fontSize:11, color:'#aaa', marginTop:2 }}>{sub}</div>}
        </div>
    );
}

function ChartCard({ title, children, height = 270, tip }) {
    return (
        <div style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:8,
            padding:'14px 16px', marginBottom:14 }}>
            <div style={{ fontWeight:600, fontSize:13, color:NAVY, marginBottom:10,
                display:'flex', alignItems:'center', gap:6 }}>
                {title}
                {tip && <Tooltip title={tip}><InfoCircleOutlined style={{ fontSize:12, color:'#aaa' }}/></Tooltip>}
            </div>
            <div style={{ height }}>{children}</div>
        </div>
    );
}

// ── Main component ────────────────────────────────────────────────────────
export default function FleetAnalyticsFuelIntelligence() {
    const [trips,     setTrips]    = useState([]);
    const [topups,    setTopups]   = useState([]);
    const [vehicles,  setVehicles] = useState([]);
    const [loading,   setLoading]  = useState(true);
    const [region,    setRegion]   = useState('');
    const [programme, setProgramme]= useState('');
    const [facility,  setFacility] = useState('');
    const [fuelType,  setFuelType] = useState('');
    const [selVeh,    setSelVeh]   = useState('');
    const [dateFrom,  setDateFrom] = useState('');
    const [dateTo,    setDateTo]   = useState('');

    const load = async () => {
        setLoading(true);
        try {
            const [r1, r2, r3] = await Promise.all([
                FleetService.getAllTripLogs(),
                FleetService.getVehiclesList(),
                FleetService.getFuelTopups ? FleetService.getFuelTopups() : Promise.resolve({ data: { code: 200, result: [] } }),
            ]);
            if (Number(r1?.data?.code) === 200)
                setTrips(r1.data.result?.data || r1.data.result || []);
            if (Number(r2?.data?.code) === 200)
                setVehicles(r2.data.result || []);
            if (Number(r3?.data?.code) === 200)
                setTopups(r3.data.result || []);
        } catch { message.error('Failed to load fuel data'); }
        setLoading(false);
    };

    useEffect(() => { load(); }, []);

    const vehicleMap = useMemo(() => {
        const m = {};
        vehicles.forEach(v => { m[String(v.id)] = v; });
        return m;
    }, [vehicles]);

    const regions    = useMemo(() => [...new Set(vehicles.map(v => v.region).filter(Boolean))], [vehicles]);
    const programmes = useMemo(() => [...new Set(vehicles.map(v => v.assigned_programme).filter(Boolean))], [vehicles]);
    const facilities = useMemo(() => [...new Set(vehicles.map(v => v.facility).filter(Boolean))], [vehicles]);
    const fuelTypes  = useMemo(() => [...new Set(vehicles.map(v => v.fuel_type).filter(Boolean))], [vehicles]);

    // ── Core: build per-vehicle running balance timeline ─────────────────
    const vehicleBalances = useMemo(() => {
        const result = [];

        const filteredVehicles = vehicles.filter(v => {
            if (region    && v.region             !== region)    return false;
            if (programme && v.assigned_programme !== programme) return false;
            if (facility  && v.facility           !== facility)  return false;
            if (fuelType  && v.fuel_type          !== fuelType)  return false;
            if (selVeh    && String(v.id)         !== selVeh)    return false;
            return true;
        });

        filteredVehicles.forEach(veh => {
            const vid      = String(veh.id);
            const baseline = getBaselineRate(veh.fuel_type, veh.engine_capacity_cc);

            // All trips for this vehicle, sorted ascending
            const vTrips = trips
                .filter(t => String(t.vehicle_id) === vid)
                .filter(t => (!dateFrom || t.trip_date >= dateFrom) && (!dateTo || t.trip_date <= dateTo))
                .sort((a, b) => a.trip_date > b.trip_date ? 1 : -1);

            // All top-ups for this vehicle, sorted ascending
            const vTopups = topups
                .filter(t => String(t.vehicle_id) === vid)
                .sort((a, b) => a.topup_date > b.topup_date ? 1 : -1);

            if (!vTrips.length && !vTopups.length) return;

            // Merge into chronological event stream
            const events = [
                ...vTrips.map(t => ({ date: t.trip_date, type: 'trip', data: t })),
                ...vTopups.map(t => ({ date: t.topup_date, type: 'topup', data: t })),
            ].sort((a, b) => a.date > b.date ? 1 : -1);

            // Running balance starts at initial_fuel_level (0 by default = all cars start empty)
            let balance   = toN(veh.initial_fuel_level);
            const timeline = [];
            let totalKm        = 0;
            let totalConsumed  = 0;
            let totalFuelled   = 0;
            let anomalies      = 0;

            events.forEach(ev => {
                if (ev.type === 'topup') {
                    const litres = toN(ev.data.litres_added);
                    balance     += litres;
                    totalFuelled += litres;
                    // Cap at tank_capacity if set
                    if (veh.tank_capacity && balance > toN(veh.tank_capacity)) {
                        balance = toN(veh.tank_capacity);
                    }
                    timeline.push({
                        date:    ev.date,
                        type:    'topup',
                        litres,
                        balance: +balance.toFixed(1),
                        flag:    'topup',
                    });
                } else {
                    const t   = ev.data;
                    // km from odometer readings; fall back to map distance
                    let km = 0;
                    if (toN(t.odometer_end) > 0 && toN(t.odometer_start) > 0) {
                        km = Math.max(0, toN(t.odometer_end) - toN(t.odometer_start));
                    } else if (toN(t.map_distance_km) > 0) {
                        km = toN(t.map_distance_km);
                    }

                    const consumed = baseline.rate > 0 ? +(km * baseline.rate / 100).toFixed(2) : 0;
                    balance       -= consumed;
                    totalKm       += km;
                    totalConsumed += consumed;

                    // Fuel recorded in trip (legacy in-trip refuel)
                    const fuelIn = toN(t.fuel_litres);
                    if (fuelIn > 0) { balance += fuelIn; totalFuelled += fuelIn; }

                    const flag = balance < 0 ? 'negative' : balance < 10 ? 'low' : 'normal';
                    if (flag !== 'normal') anomalies++;

                    timeline.push({
                        date:     t.trip_date,
                        type:     'trip',
                        km:       +km.toFixed(1),
                        consumed: +consumed.toFixed(2),
                        fuelIn,
                        balance:  +balance.toFixed(1),
                        flag,
                        purpose:  t.purpose,
                        driver:   t.driver_name,
                    });
                }
            });

            result.push({
                vehicle_id:    vid,
                reg:           veh.registration_no || veh.serial_no || vid,
                vehicleType:   veh.vehicle_type || '—',
                region:        veh.region,
                programme:     veh.assigned_programme,
                facility:      veh.facility,
                fuelType:      veh.fuel_type,
                engineCC:      veh.engine_capacity_cc,
                baseline:      baseline.rate,
                baselineLabel: baseline.label,
                initialLevel:  toN(veh.initial_fuel_level),
                tankCapacity:  veh.tank_capacity ? toN(veh.tank_capacity) : null,
                currentBalance: +balance.toFixed(1),
                totalKm:       +totalKm.toFixed(1),
                totalConsumed: +totalConsumed.toFixed(2),
                totalFuelled:  +totalFuelled.toFixed(1),
                anomalies,
                timeline,
            });
        });

        return result.sort((a, b) => a.currentBalance - b.currentBalance);
    }, [trips, topups, vehicles, vehicleMap, region, programme, facility, fuelType, selVeh, dateFrom, dateTo]);

    // ── Derived KPIs ──────────────────────────────────────────────────────
    const kpis = useMemo(() => {
        const negative    = vehicleBalances.filter(v => v.currentBalance < 0);
        const low         = vehicleBalances.filter(v => v.currentBalance >= 0 && v.currentBalance < 10);
        const totalFuelled   = vehicleBalances.reduce((s, v) => s + v.totalFuelled, 0);
        const totalConsumed  = vehicleBalances.reduce((s, v) => s + v.totalConsumed, 0);
        const totalKm        = vehicleBalances.reduce((s, v) => s + v.totalKm, 0);
        const unaccounted    = vehicleBalances.reduce((s, v) => s + Math.max(0, -v.currentBalance), 0);
        const totalAnomalies = vehicleBalances.reduce((s, v) => s + v.anomalies, 0);
        return { negative, low, totalFuelled, totalConsumed, totalKm, unaccounted, totalAnomalies };
    }, [vehicleBalances]);

    // ── Monthly consumption trend (total fleet) ───────────────────────────
    const monthlyTrend = useMemo(() => {
        const m = {};
        vehicleBalances.forEach(v => {
            v.timeline.filter(e => e.type === 'trip' && e.km > 0).forEach(e => {
                const mo = moment(e.date).format('MMM YY');
                if (!m[mo]) m[mo] = { month: mo, consumed: 0, km: 0, fuelled: 0 };
                m[mo].consumed += e.consumed;
                m[mo].km       += e.km;
                if (e.fuelIn) m[mo].fuelled += e.fuelIn;
            });
            v.timeline.filter(e => e.type === 'topup').forEach(e => {
                const mo = moment(e.date).format('MMM YY');
                if (!m[mo]) m[mo] = { month: mo, consumed: 0, km: 0, fuelled: 0 };
                m[mo].fuelled += e.litres;
            });
        });
        return Object.values(m)
            .sort((a, b) => moment(a.month, 'MMM YY') - moment(b.month, 'MMM YY'))
            .slice(-12)
            .map(mo => ({
                ...mo,
                consumed:  +mo.consumed.toFixed(1),
                fuelled:   +mo.fuelled.toFixed(1),
                efficiency: mo.km > 0 ? +(mo.consumed / mo.km * 100).toFixed(1) : 0,
            }));
    }, [vehicleBalances]);

    // ── Current balance bar chart ─────────────────────────────────────────
    const balanceBarData = useMemo(() =>
        vehicleBalances.map(v => ({
            name:    v.reg,
            balance: v.currentBalance,
            color:   v.currentBalance < 0 ? '#f5222d' : v.currentBalance < 10 ? '#fa8c16' : '#52c41a',
        }))
    , [vehicleBalances]);

    const exportXlsx = () => {
        const rows = [];
        vehicleBalances.forEach(v => {
            v.timeline.forEach(e => {
                rows.push({
                    'Reg No':          v.reg,
                    'Vehicle Type':    v.vehicleType,
                    'Fuel Type':       v.fuelType,
                    'Engine CC':       v.engineCC,
                    'Baseline L/100km':v.baseline,
                    'Date':            e.date,
                    'Event Type':      e.type,
                    'KM Driven':       e.km || '',
                    'Consumed (L)':    e.consumed || '',
                    'Fuel Added (L)':  e.fuelIn || e.litres || '',
                    'Tank Balance (L)': e.balance,
                    'Flag':            e.flag || '',
                });
            });
        });
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Fuel Balance');
        saveAs(new Blob([XLSX.write(wb, { bookType:'xlsx', type:'array' })]),
            `FuelIntelligence_${new Date().toISOString().slice(0,10)}.xlsx`);
    };

    const filterBar = (
        <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:16, alignItems:'center' }}>
            <Select allowClear placeholder="Region" size="small" style={{ width:120 }}
                value={region || undefined} onChange={v => setRegion(v || '')}>
                {regions.map(r => <Select.Option key={r} value={r}>{r}</Select.Option>)}
            </Select>
            <Select allowClear placeholder="Programme" size="small" style={{ width:140 }}
                value={programme || undefined} onChange={v => setProgramme(v || '')}>
                {programmes.map(p => <Select.Option key={p} value={p}>{p}</Select.Option>)}
            </Select>
            <Select allowClear placeholder="Facility" size="small" style={{ width:150 }}
                value={facility || undefined} onChange={v => setFacility(v || '')}>
                {facilities.map(f => <Select.Option key={f} value={f}>{f}</Select.Option>)}
            </Select>
            <Select allowClear placeholder="Fuel type" size="small" style={{ width:110 }}
                value={fuelType || undefined} onChange={v => setFuelType(v || '')}>
                {fuelTypes.map(f => <Select.Option key={f} value={f}>{f}</Select.Option>)}
            </Select>
            <Select allowClear placeholder="Vehicle" size="small" style={{ width:130 }}
                showSearch value={selVeh || undefined} onChange={v => setSelVeh(v || '')}
                filterOption={(i, o) => o.children.toLowerCase().includes(i.toLowerCase())}>
                {vehicles.map(v => (
                    <Select.Option key={String(v.id)} value={String(v.id)}>
                        {v.registration_no}
                    </Select.Option>
                ))}
            </Select>
            <DatePicker size="small" placeholder="From" style={{ width:120 }}
                onChange={(_, s) => setDateFrom(s || '')}/>
            <DatePicker size="small" placeholder="To" style={{ width:120 }}
                onChange={(_, s) => setDateTo(s || '')}/>
            <Button size="small" icon={<ReloadOutlined/>} onClick={load}>Refresh</Button>
            <Button size="small" type="primary" icon={<DownloadOutlined/>}
                onClick={exportXlsx} disabled={!vehicleBalances.length}>Export</Button>
        </div>
    );

    return (
        <div className="page-content" style={{ padding:'20px 24px' }}>
            <div style={{ marginBottom:16 }}>
                <h4 style={{ margin:0, fontWeight:700, color:NAVY }}>Fuel Intelligence Dashboard</h4>
                <div style={{ fontSize:12, color:'#888', marginTop:2 }}>
                    Running tank balance · estimated from km driven × engine baseline rate.
                    Fuel added = top-up, not consumption.
                    Negative balance = investigate.
                </div>
            </div>

            {filterBar}

            <Spin spinning={loading}>
                {/* ── KPI row ── */}
                <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' }}>
                    <KPI label="Vehicles tracked"
                        value={vehicleBalances.length} color={NAVY}/>
                    <KPI label="Negative balance"
                        value={kpis.negative.length} color={kpis.negative.length > 0 ? '#f5222d' : '#52c41a'}
                        sub="impossible — investigate" warn={kpis.negative.length > 0}/>
                    <KPI label="Low tank (< 10L)"
                        value={kpis.low.length} color={kpis.low.length > 0 ? '#fa8c16' : '#52c41a'}
                        sub="prompt top-up"/>
                    <KPI label="Total fuelled"
                        value={`${kpis.totalFuelled.toFixed(0)} L`} color="#1890ff"/>
                    <KPI label="Estimated consumed"
                        value={`${kpis.totalConsumed.toFixed(0)} L`} color="#389e0d"
                        sub="km × baseline rate"/>
                    <KPI label="Unaccounted fuel"
                        value={`${kpis.unaccounted.toFixed(1)} L`}
                        color={kpis.unaccounted > 0 ? '#f5222d' : '#52c41a'}
                        sub="sum of negatives" warn={kpis.unaccounted > 0}/>
                </div>

                {/* ── Alert bar ── */}
                {(kpis.negative.length > 0 || kpis.low.length > 0) && (
                    <div style={{ background:'#fff2f0', border:'1px solid #ffa39e', borderRadius:8,
                        padding:'10px 16px', marginBottom:14, display:'flex', gap:16, alignItems:'center' }}>
                        <WarningOutlined style={{ color:'#f5222d', fontSize:18 }}/>
                        <div>
                            {kpis.negative.length > 0 && (
                                <span style={{ fontWeight:600, color:'#a8071a', marginRight:16 }}>
                                    {kpis.negative.map(v => v.reg).join(', ')} —
                                    negative balance: unrecorded refuel or fuel theft
                                </span>
                            )}
                            {kpis.low.length > 0 && (
                                <span style={{ color:'#d46b08' }}>
                                    {kpis.low.map(v => v.reg).join(', ')} — below 10L, prompt driver to top up
                                </span>
                            )}
                        </div>
                    </div>
                )}

                {/* ── Current balance per vehicle ── */}
                {balanceBarData.length > 0 && (
                    <ChartCard title="Estimated current tank balance per vehicle" height={Math.max(200, balanceBarData.length * 32)}
                        tip="Calculated from: starting level (0L) + all top-ups − (all km × baseline L/100km). Green = OK · Orange = below 10L · Red = negative (investigate)">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={balanceBarData} layout="vertical"
                                margin={{ top:0, right:60, left:10, bottom:0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" horizontal={false}/>
                                <XAxis type="number" tick={{ fontSize:10 }} tickFormatter={v => `${v}L`}/>
                                <YAxis type="category" dataKey="name" tick={{ fontSize:11 }} width={90}/>
                                <RTooltip formatter={v => [`${v} L estimated`, 'Tank balance']}/>
                                <ReferenceLine x={10} stroke="#fa8c16" strokeDasharray="4 2"
                                    label={{ value:'10L', position:'insideTopRight', fontSize:9, fill:'#fa8c16' }}/>
                                <ReferenceLine x={0}  stroke="#f5222d" strokeWidth={2}/>
                                <Bar dataKey="balance" radius={[0,4,4,0]}>
                                    {balanceBarData.map((d, i) => <Cell key={i} fill={d.color}/>)}
                                    <LabelList dataKey="balance" position="right"
                                        style={{ fontSize:10 }} formatter={v => `${v}L`}/>
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>
                )}

                {/* ── Monthly trend: consumed vs fuelled ── */}
                {monthlyTrend.length > 0 && (
                    <ChartCard title="Monthly fuel consumed vs topped up (litres)" height={260}
                        tip="Consumed = km driven × baseline rate (what the engine should have used). Fuelled = what was actually added to tanks. If fuelled < consumed consistently, some fuel is unaccounted for.">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={monthlyTrend} margin={{ top:5, right:20, left:5, bottom:5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                                <XAxis dataKey="month" tick={{ fontSize:10 }}/>
                                <YAxis tick={{ fontSize:10 }} tickFormatter={v => `${v}L`}/>
                                <RTooltip formatter={(v, n) => [`${v} L`, n]}/>
                                <Legend wrapperStyle={{ fontSize:11 }}/>
                                <Bar dataKey="consumed" name="Estimated consumed (L)"
                                    fill="#1a3a5c" radius={[4,4,0,0]} opacity={0.8}/>
                                <Bar dataKey="fuelled"  name="Actual topped up (L)"
                                    fill="#1890ff" radius={[4,4,0,0]} opacity={0.8}/>
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>
                )}

                {/* ── Per-vehicle detail table ── */}
                {vehicleBalances.length > 0 && (
                    <div style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:8,
                        overflow:'hidden', marginBottom:14 }}>
                        <div style={{ background:NAVY, color:'#fff', padding:'10px 16px',
                            fontWeight:600, fontSize:13 }}>
                            Per-vehicle summary
                        </div>
                        <div style={{ overflowX:'auto' }}>
                            <table style={{ width:'100%', fontSize:12, borderCollapse:'collapse' }}>
                                <thead>
                                    <tr style={{ background:'#f5f5f5' }}>
                                        {['Vehicle','Type','Fuel','Baseline','Start','KM Driven',
                                          'Est. Consumed','Topped Up','Current Balance','Flags',''].map((h,i) => (
                                            <th key={i} style={{ padding:'8px 10px', fontSize:11,
                                                fontWeight:600, textAlign: i >= 5 ? 'right' : 'left',
                                                borderBottom:'1px solid #e8e8e8' }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {vehicleBalances.map((v, i) => (
                                        <tr key={v.vehicle_id} style={{
                                            background: i%2===0 ? '#fff' : '#fafafa',
                                            borderBottom:'1px solid #f0f0f0',
                                            borderLeft: v.currentBalance < 0 ? '3px solid #f5222d'
                                                : v.currentBalance < 10 ? '3px solid #fa8c16'
                                                : '3px solid transparent',
                                        }}>
                                            <td style={{ padding:'8px 10px', fontWeight:600 }}>{v.reg}</td>
                                            <td style={{ padding:'8px 10px', color:'#888', fontSize:11 }}>{v.vehicleType}</td>
                                            <td style={{ padding:'8px 10px' }}>{v.fuelType || '—'}</td>
                                            <td style={{ padding:'8px 10px', textAlign:'right' }}>
                                                <Tag color="blue" style={{ fontSize:10 }}>{v.baseline} L/100km</Tag>
                                            </td>
                                            <td style={{ padding:'8px 10px', textAlign:'right', color:'#888' }}>
                                                {fmtL(v.initialLevel)}
                                            </td>
                                            <td style={{ padding:'8px 10px', textAlign:'right' }}>
                                                {v.totalKm.toLocaleString()} km
                                            </td>
                                            <td style={{ padding:'8px 10px', textAlign:'right', color:'#389e0d' }}>
                                                {fmtL(v.totalConsumed)}
                                            </td>
                                            <td style={{ padding:'8px 10px', textAlign:'right', color:'#1890ff' }}>
                                                {fmtL(v.totalFuelled)}
                                            </td>
                                            <td style={{ padding:'8px 10px', textAlign:'right' }}>
                                                <span style={{ fontWeight:700,
                                                    color: v.currentBalance < 0 ? '#f5222d'
                                                        : v.currentBalance < 10 ? '#fa8c16' : '#52c41a' }}>
                                                    {fmtL(v.currentBalance)}
                                                </span>
                                            </td>
                                            <td style={{ padding:'8px 10px', textAlign:'right' }}>
                                                {v.anomalies > 0
                                                    ? <Tag color="red" style={{ fontSize:10 }}>{v.anomalies}</Tag>
                                                    : <CheckCircleOutlined style={{ color:'#52c41a' }}/>}
                                            </td>
                                            <td style={{ padding:'8px 10px', textAlign:'center', whiteSpace:'nowrap' }}>
                                                {v.currentBalance < 10 && (
                                                    <Tooltip title={v.currentBalance < 0
                                                        ? 'Negative balance — check for unrecorded refuel or fuel theft'
                                                        : 'Below 10L — driver should record a top-up'}>
                                                        <WarningOutlined style={{
                                                            color: v.currentBalance < 0 ? '#f5222d' : '#fa8c16',
                                                            marginRight:4 }}/>
                                                    </Tooltip>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* ── Anomaly events list ── */}
                {kpis.totalAnomalies > 0 && (
                    <div style={{ background:'#fff', border:'1px solid #e8e8e8',
                        borderRadius:8, overflow:'hidden' }}>
                        <div style={{ background:NAVY, color:'#fff', padding:'10px 16px',
                            fontWeight:600, fontSize:13 }}>
                            ⚠ Balance anomaly events — {kpis.totalAnomalies} total
                        </div>
                        <div style={{ overflowX:'auto' }}>
                            <table style={{ width:'100%', fontSize:12, borderCollapse:'collapse' }}>
                                <thead>
                                    <tr style={{ background:'#f5f5f5' }}>
                                        {['Vehicle','Date','KM','Est. Used','Balance After','Flag','Action'].map((h,i) => (
                                            <th key={i} style={{ padding:'8px 10px', fontSize:11, fontWeight:600,
                                                borderBottom:'1px solid #e8e8e8' }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {vehicleBalances.flatMap(v =>
                                        v.timeline
                                            .filter(e => e.flag === 'negative' || e.flag === 'low')
                                            .map((e, i) => (
                                                <tr key={`${v.vehicle_id}-${i}`} style={{
                                                    background: e.flag === 'negative' ? '#fff2f0' : '#fff9f0',
                                                    borderBottom:'1px solid #f5f5f5' }}>
                                                    <td style={{ padding:'7px 10px', fontWeight:600 }}>{v.reg}</td>
                                                    <td style={{ padding:'7px 10px' }}>{e.date}</td>
                                                    <td style={{ padding:'7px 10px', textAlign:'right' }}>{e.km} km</td>
                                                    <td style={{ padding:'7px 10px', textAlign:'right', color:'#389e0d' }}>
                                                        −{fmtL(e.consumed)}
                                                    </td>
                                                    <td style={{ padding:'7px 10px', textAlign:'right', fontWeight:700,
                                                        color: e.flag === 'negative' ? '#f5222d' : '#fa8c16' }}>
                                                        {fmtL(e.balance)}
                                                    </td>
                                                    <td style={{ padding:'7px 10px' }}>
                                                        <Tag color={e.flag === 'negative' ? 'red' : 'orange'}
                                                            style={{ fontSize:10 }}>
                                                            {e.flag === 'negative' ? 'Investigate' : 'Low — top up'}
                                                        </Tag>
                                                    </td>
                                                    <td style={{ padding:'7px 10px', fontSize:11, color:'#888' }}>
                                                        {e.flag === 'negative'
                                                            ? 'Check for unrecorded refuel or siphoning'
                                                            : 'Prompt driver to log top-up before next trip'}
                                                    </td>
                                                </tr>
                                            ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {!loading && vehicleBalances.length === 0 && (
                    <div style={{ textAlign:'center', padding:60, color:'#aaa',
                        border:'2px dashed #e8e8e8', borderRadius:8 }}>
                        <div style={{ fontSize:32, marginBottom:12 }}>⛽</div>
                        <div style={{ fontWeight:600, marginBottom:4 }}>No fuel data found</div>
                        <div style={{ fontSize:12 }}>
                            Log trips with odometer readings, and record fuel top-ups in the Fuel Top-ups page.
                        </div>
                    </div>
                )}
            </Spin>
        </div>
    );
}